import React, { useState, useEffect } from "react"
import { Box, Typography, TextField, Button, Paper, CircularProgress } from "@mui/material"
import { doc, getDoc, updateDoc, arrayUnion, onSnapshot, runTransaction, serverTimestamp } from "firebase/firestore"
import { db } from "../../../lib/firebase"
import { useNavigate } from "react-router-dom"

const BuzzerClient = () => {
  const navigate = useNavigate()
  
  // State
  const [step, setStep] = useState("JOIN") // JOIN, GAME
  const [code, setCode] = useState("")
  const [name, setName] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  
  // Game State
  const [sessionData, setSessionData] = useState(null)

  // Join Game
  const handleJoin = async () => {
    if (!code || !name) {
      setError("Please enter both code and name")
      return
    }
    setLoading(true)
    setError("")

    try {
      const formattedCode = code.toUpperCase()
      const sessionRef = doc(db, "buzzer_sessions", formattedCode)
      const sessionSnap = await getDoc(sessionRef)

      if (sessionSnap.exists()) {
        // Add player to session
        await updateDoc(sessionRef, {
          players: arrayUnion(name)
        })
        
        // Subscribe
        const unsubscribe = onSnapshot(sessionRef, (doc) => {
           setSessionData(doc.data())
        })

        // Store unsubscribe somehow?  For now, we'll re-subscribe in a useEffect based on step changing to GAME
        // Actually, let's just change step and do the subscription in a separate useEffect that depends on 'step'
        setCode(formattedCode) // ensure upper case
        setStep("GAME")
      } else {
        setError("Game code not found")
      }
    } catch (err) {
      setError("Failed to join. Try again.")
    } finally {
      setLoading(false)
    }
  }

  // Effect to manage subscription when in GAME mode
  useEffect(() => {
    if (step === "GAME" && code) {
       const sessionRef = doc(db, "buzzer_sessions", code)
       const unsubscribe = onSnapshot(sessionRef, (doc) => {
         if (doc.exists()) {
           setSessionData(doc.data())
         } else {
           // Session deleted?
           setError("Session ended by host")
           setStep("JOIN")
         }
       })
       return () => unsubscribe()
    }
  }, [step, code])

  // Lock scrolling when in GAME mode
  useEffect(() => {
    if (step === "GAME") {
      // Prevent body scroll
      document.body.style.overflow = 'hidden'
      document.body.style.position = 'fixed'
      document.body.style.width = '100%'
      document.body.style.height = '100%'
      
      // Prevent overscroll/bounce on iOS
      document.documentElement.style.overflow = 'hidden'
      document.documentElement.style.position = 'fixed'
      document.documentElement.style.width = '100%'
      document.documentElement.style.height = '100%'
      
      return () => {
        // Cleanup on unmount
        document.body.style.overflow = ''
        document.body.style.position = ''
        document.body.style.width = ''
        document.body.style.height = ''
        document.documentElement.style.overflow = ''
        document.documentElement.style.position = ''
        document.documentElement.style.width = ''
        document.documentElement.style.height = ''
      }
    }
  }, [step])


  // Handle Buzz
  const handleBuzz = async () => {
    if (!sessionData || sessionData.buzzed_user) return

    // Haptic feedback if available
    if (navigator.vibrate) navigator.vibrate(200);

    try {
       await runTransaction(db, async (transaction) => {
         const sessionRef = doc(db, "buzzer_sessions", code)
         const sessionDoc = await transaction.get(sessionRef)
         
         if (!sessionDoc.exists()) {
           throw "Document does not exist!"
         }
         
         const data = sessionDoc.data()
         if (data.buzzed_user === null) {
           transaction.update(sessionRef, {
             buzzed_user: name,
             buzzed_at: serverTimestamp()
           })
         }
       })
    } catch (e) {
    }
  }

  // Render Join Screen
  if (step === "JOIN") {
    return (
      <Box sx={{ 
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,  // Padding ensures content doesn't touch edges
        boxSizing: "border-box",
        bgcolor: "#07090e",
      }}>
        <Paper sx={{ 
          p: 4, 
          textAlign: "center",
          width: "100%",
          maxWidth: "350px",  // Smaller than iPhone SE (375px) to ensure it fits
          boxSizing: "border-box",
          bgcolor: "rgba(255, 255, 255, 0.04)",
          color: "#fff",
          border: "1px solid rgba(255, 255, 255, 0.08)",
          backdropFilter: "blur(16px)",
          borderRadius: "20px",
        }}>
          <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ fontFamily: "'Playfair Display', serif", mb: 3 }}>Join Game</Typography>
          <Box component="form" sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <TextField 
              label="Game Code" 
              variant="outlined" 
              value={code} 
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              inputProps={{ maxLength: 4, style: { textTransform: "uppercase", letterSpacing: 4, textAlign: "center", fontSize: "1.5rem", color: "#fff" } }}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
                  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                },
                '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' }
              }}
            />
            <TextField 
              label="Your Name" 
              variant="outlined" 
              value={name} 
              onChange={(e) => setName(e.target.value)}
              fullWidth
              inputProps={{ style: { color: "#fff" } }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
                  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                },
                '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' }
              }}
            />
            {error && <Typography color="error">{error}</Typography>}
            <Button 
              variant="contained" 
              size="large" 
              onClick={handleJoin}
              disabled={loading}
              sx={{ py: 1.5, fontSize: "1.1rem", bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" } }}
              fullWidth
            >
              {loading ? <CircularProgress size={24} /> : "JOIN"}
            </Button>
          </Box>
        </Paper>
      </Box>
    )
  }

  // Helper for consistent colors
  const getColorFromName = (name) => {
    const colors = [
      "#FF5733", "#33FF57", "#3357FF", "#FF33A1", "#FFD700", 
      "#00FFFF", "#FF8C00", "#8A2BE2", "#00FA9A", "#FF1493"
    ]
    let hash = 0
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash)
    }
    const index = Math.abs(hash % colors.length)
    return colors[index]
  }

  // Game Render Logic
  const myBuzz = sessionData?.buzzed_user === name
  const someoneElseBuzz = sessionData?.buzzed_user && !myBuzz
  const locked = !!sessionData?.buzzed_user

  let bgColor = getColorFromName(name) // Player's specific color
  let text = "BUZZ!"
  
  if (myBuzz) {
    bgColor = "success.main"
    text = "YOU BUZZED!"
  } else if (someoneElseBuzz) {
    bgColor = "error.main"
    text = "LOCKED"
  }

  return (
    <>
      {/* Global styles to prevent ANY scrolling */}
      <style>{`
        html, body {
          overflow: hidden !important;
          overscroll-behavior: none !important;
          touch-action: manipulation !important;
        }
      `}</style>
      
      {/* Main container - use inset:0 instead of 100vw to avoid scrollbar issue */}
      <Box sx={{ 
        position: "fixed",
        inset: 0,  // This is equivalent to top:0, right:0, bottom:0, left:0
        bgcolor: "#07090e",
        color: "#fff",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}>
        
        {/* Header Row */}
        <Box sx={{ 
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          py: 1,
          bgcolor: "rgba(255,255,255,0.05)",
        }}>
          <Typography 
            sx={{ 
              fontWeight: "bold",
              fontSize: "1rem",
              maxWidth: "50%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}
          >
            {name}
          </Typography>
          <Typography sx={{ fontSize: "0.9rem", opacity: 0.7 }}>
            {code}
          </Typography>
        </Box>

        {/* Button Area */}
        <Box sx={{ 
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 0,
          overflow: "hidden",
        }}>
          <Button
            variant="contained"
            onClick={handleBuzz}
            disabled={locked}
            sx={{
              // Use percentage of container instead of viewport units
              width: "65%",
              maxWidth: "300px",
              aspectRatio: "1 / 1",  // Keep it circular
              borderRadius: "50%",
              fontSize: "clamp(1.5rem, 6vmin, 3rem)",
              fontWeight: "bold",
              bgcolor: bgColor,
              boxShadow: "0px 8px 24px rgba(0,0,0,0.3)",
              transition: "transform 0.1s, filter 0.1s",
              "&:hover": {
                bgcolor: bgColor,
                filter: "brightness(0.95)"
              },
              "&:active": {
                transform: locked ? "none" : "scale(0.95)"
              },
              "&:disabled": {
                bgcolor: myBuzz ? "success.main" : "error.main",
                color: "white"
              }
            }}
          >
            {text}
          </Button>
        </Box>

        {/* Status Row */}
        <Box sx={{ 
          height: "60px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          px: 2,
        }}>
          {someoneElseBuzz && (
            <Typography 
              sx={{ 
                fontWeight: "bold", 
                color: "error.main",
                fontSize: "1.1rem",
                textAlign: "center"
              }}
            >
              {sessionData.buzzed_user} was faster!
            </Typography>
          )}
        </Box>
        
      </Box>
    </>
  )
}

export default BuzzerClient
